import { useContext, useEffect, useState } from 'react';
import { GlobalContext } from '../context/GlobalContext';
import {
	ArrowUpDown,
	Building2,
	ChevronDown,
	Clock,
	Edit,
	ImageUp,
	Lock,
	Mail,
	Map,
	MapPin,
	Plus,
	Power,
	Store,
	Trash2
} from 'lucide-react';
import { Button } from '../shadcn/Button';
import { Dialog } from '../components/Dialog';
import Input from '../components/Input';
import Select from '../components/Select';
import Combobox from '../components/Combobox';
import { validate_email, validate_phone_number } from '../utils/validate';
import useFetch, { API, callAPI } from '../hooks/api';
import Spinner, { ContainerSpinner } from '../components/Spinner';
import { Instagram, WhatsApp } from '@material-ui/icons';
import ErrorRedirectDialog from '../components/ErrorRedirectDialog';
import { Navigate } from 'react-router-dom';
import classNames from 'classnames';
import Firebase from '../firebase_setup';
import firestore from '../firestore';
import * as Accordion from '@radix-ui/react-accordion';
import PhoneInput from '../components/PhoneInput';
import GoogleAddressInput from '../components/GoogleAddressInput';
import { Checkbox } from '../shadcn/Checkbox';

const SELLER_PERMISSIONS = [
	{ key: 'new_sale', label: 'Nuevo registro' },
	{ key: 'delete_sale', label: 'Eliminar ventas' },
	{ key: 'delete_payment', label: 'Eliminar pagos' },
	{ key: 'change_sale_price', label: 'Cambiar precio de venta' },
	{ key: 'register_payment', label: 'Registrar pagos' },
	{ key: 'recargas', label: 'Recargas' }
] as const;

const ALL_PERMISSION_KEYS = SELLER_PERMISSIONS.map((p) => p.key);

// Convert blacklist to allowed permissions (for UI display)
const blacklistToAllowed = (blacklist: string[], inheritedBlacklist: string[] = []): string[] => {
	const combinedBlacklist = new Set([...blacklist, ...inheritedBlacklist]);
	return ALL_PERMISSION_KEYS.filter((key) => !combinedBlacklist.has(key));
};

// Convert allowed permissions to blacklist (for storage)
const allowedToBlacklist = (allowed: string[]): string[] => {
	return ALL_PERMISSION_KEYS.filter((key) => !allowed.includes(key));
};

const WHITELISTED_PERMISSIONS = [
	{ key: 'cobranza_portal', label: 'Cobranza' },
	{ key: 'promotions_portal', label: 'Promociones' }
] as const;

const ALL_PERMISSIONS = [
	...SELLER_PERMISSIONS.map((p) => ({ ...p, type: 'blacklist' as const })),
	...WHITELISTED_PERMISSIONS.map((p) => ({ ...p, type: 'whitelist' as const }))
];

type PermissionsBlacklist = {
	all_sellers?: string[];
	[email: string]: string[] | undefined;
};

type PermissionsWhitelist = PermissionsBlacklist;

type SucursalType = Omit<API['https://api.krece.app/storeprofile/branch/']['POST']['request'], 'email'> &
	API['https://api.krece.app/storeprofile/branch/']['GET']['response'][string]['sucursales'][number];


// Moved outside of Sucursales to prevent component recreation on parent re-render
function UpsertSucursalDialog({
	isNew = false,
	image = false,
	selectedBranch,
	setSelectedBranch,
	onboarding,
	updateRegistrationSteps,
	refetch,
	masterStoreId,
	country,
	cities,
	data,
	setUploadingImage
}: {
	isNew?: boolean;
	image?: boolean;
	selectedBranch: SucursalType | undefined;
	setSelectedBranch: (branch: SucursalType | undefined) => void;
	onboarding?: boolean;
	updateRegistrationSteps?: () => void;
	refetch: () => void;
	masterStoreId: string;
	country: string;
	cities: string[];
	data: API['https://api.krece.app/storeprofile/branch/']['GET']['response'] | null;
	setUploadingImage: (value: false | string) => void;
}) {
	if (selectedBranch && typeof selectedBranch.hours_of_operation !== 'object') {
		selectedBranch.hours_of_operation = {
			Lunes: { active: false, from: 9, to: 5 },
			Martes: { active: false, from: 9, to: 5 },
			Miércoles: { active: false, from: 9, to: 5 },
			Jueves: { active: false, from: 9, to: 5 },
			Viernes: { active: false, from: 9, to: 5 },
			Sábado: { active: false, from: 9, to: 5 },
			Domingo: { active: false, from: 9, to: 5 }
		};
	}
	if (selectedBranch && !selectedBranch.location_coordinates) {
		selectedBranch.location_coordinates = undefined;
	}

	const [rerender, setRerender] = useState(0);
	const [submit, setSubmit] = useState(false);
	const { loading, error, setPayload, fetched } = useFetch(
		'https://api.krece.app/storeprofile/branch/',
		!isNew ? 'PUT' : 'POST',
		submit
	);

	const [validationError, setValidationError] = useState(false);
	const emailValidation = (value: string) => validate_email(value.trim());
	const nameValidation = (value: string) => value.trim().length >= 3;
	const addressValidation = (value: string) => value.trim().length >= 3;
	const coordinatesValidation = (coordinates: { latitude: number; longitude: number } | undefined) => !!coordinates;
	const operatingHoursValidation = (value: { [key: string]: { active: boolean; from: number; to: number } }) =>
		Object.values(value).some((day) => day.active);
	const cityValidation = (value: string) => value.trim();
	const phoneValidation = (value: string) => validate_phone_number(value, country);
	const instagramValidation = (value: string) => value.trim().length >= 4;

	function submitChanges() {
		if (!selectedBranch) return;

		if (
			!emailValidation(selectedBranch.store_email) ||
			!nameValidation(selectedBranch.store_name) ||
			!addressValidation(selectedBranch?.location_details || '') ||
			!coordinatesValidation(selectedBranch.location_coordinates) ||
			!operatingHoursValidation(selectedBranch.hours_of_operation) ||
			!cityValidation(selectedBranch.city) ||
			(selectedBranch.whatsapp && !phoneValidation(selectedBranch.whatsapp)) ||
			(selectedBranch.instagram && !instagramValidation(selectedBranch.instagram))
		) {
			setValidationError(true);
		} else {
			setPayload({ email: masterStoreId, ...selectedBranch });
			setSubmit(true);
		}
	}

	useEffect(() => {
		if (fetched && !error) {
			setSelectedBranch(undefined);

			if (!onboarding) refetch();
			else updateRegistrationSteps?.();

			if (image) setUploadingImage(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fetched, error]);

	useEffect(() => {
		if (image && selectedBranch) {
			setPayload({ email: masterStoreId, ...selectedBranch });
			setSubmit(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (selectedBranch?.hours_of_operation && typeof selectedBranch.hours_of_operation === 'object') {
			// Order the days of the week consistently
			const orderedDays = {
				Lunes: selectedBranch.hours_of_operation.Lunes,
				Martes: selectedBranch.hours_of_operation.Martes,
				Miércoles: selectedBranch.hours_of_operation.Miércoles,
				Jueves: selectedBranch.hours_of_operation.Jueves,
				Viernes: selectedBranch.hours_of_operation.Viernes,
				Sábado: selectedBranch.hours_of_operation.Sábado,
				Domingo: selectedBranch.hours_of_operation.Domingo
			};

			selectedBranch.hours_of_operation = orderedDays;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedBranch]);

	if (!selectedBranch) return null;
	if (image) return null;

	return (
		<>
			{validationError && (
				<Dialog title="¡Error!" onClose={() => setValidationError(false)} zIndex={51}>
					<p>Los siguientes campos están vacíos o son inválidos:</p>
					<ul className="text-red-400">
						{!emailValidation(selectedBranch.store_email) && <li className="list-inside list-disc">Email</li>}
						{!nameValidation(selectedBranch.store_name) && <li className="list-inside list-disc">Nombre</li>}
						{!cityValidation(selectedBranch.city) && <li className="list-inside list-disc">Ciudad</li>}
						{!coordinatesValidation(selectedBranch.location_coordinates) && (
							<li className="list-inside list-disc">Coordenadas (debe seleccionar ubicación exacta)</li>
						)}
						{!addressValidation(selectedBranch.location_details || '') && (
							<li className="list-inside list-disc">Dirección</li>
						)}
						{selectedBranch.whatsapp && !phoneValidation(selectedBranch.whatsapp) && (
							<li className="list-inside list-disc">WhatsApp</li>
						)}
						{selectedBranch.instagram && !instagramValidation(selectedBranch.instagram) && (
							<li className="list-inside list-disc">Instagram</li>
						)}
						{!operatingHoursValidation(selectedBranch.hours_of_operation) && (
							<li className="list-inside list-disc">Horario de Atención</li>
						)}
					</ul>
				</Dialog>
			)}
			{error && (
				<Dialog title="¡Error!" zIndex={52}>
					<p>{typeof error === 'string' ? error : 'Ha ocurrido un error. Intente más tarde.'}</p>
				</Dialog>
			)}

			<Dialog title={isNew ? 'Nuevo Local' : 'Editar Local'} closable={false}>
				{loading && (
					<div className="absolute inset-0 z-50 flex h-full w-full items-center justify-center rounded-xl bg-black/70 light:bg-black/20">
						<ContainerSpinner size={2.75} />
					</div>
				)}

				<form
					className="w-[400px] max-w-full space-y-6"
					onSubmit={(e) => {
						e.preventDefault();
						submitChanges();
					}}
				>
					<div className="mt-1 flex flex-col gap-4">
						<Input
							readonly={!isNew}
							value={selectedBranch.store_email.trim()}
							floatingLabel="Email"
							placeholder="Ingrese el email del local"
							Icon={<Mail className="size-4" />}
							validation={emailValidation}
							onChange={(value) => (selectedBranch.store_email = value.trim())}
						/>
						<Input
							readonly={!isNew}
							value={selectedBranch.store_name.trim()}
							floatingLabel="Nombre"
							placeholder="Ingrese el nombre del local"
							Icon={<Store className="size-4" />}
							validation={nameValidation}
							onChange={(value) => (selectedBranch.store_name = value.trim())}
						/>
						<Combobox
							options={cities.map((e) => ({ label: e, value: e }))}
							defaultValue={selectedBranch.city || undefined}
							onSelectedChange={(value) => (selectedBranch.city = typeof value === 'string' ? value.trim() : '')}
							Icon={<Map className="size-4" />}
							floatingLabel="Ciudad"
							placeholder="Elige la ciudad"
						/>
						<GoogleAddressInput
							initialAddress={selectedBranch.location_details || ''}
							initialCoordinates={
								selectedBranch.location_coordinates
									? { lat: selectedBranch.location_coordinates.latitude, lng: selectedBranch.location_coordinates.longitude }
									: null
							}
							country={country}
							onChange={(address, coordinates) => {
								selectedBranch.location_details = address?.trim() || '';
								if (coordinates) {
									selectedBranch.location_coordinates = {
										latitude: coordinates.lat,
										longitude: coordinates.lng
									};
								} else {
									selectedBranch.location_coordinates = undefined;
								}
							}}
							className="mb-1"
						/>
						<PhoneInput
							value={selectedBranch?.whatsapp || data?.[selectedBranch?.brand]?.info?.whatsapp || undefined}
							country={country}
							onChange={(value) => (selectedBranch.whatsapp = value.trim())}
						/>
						<Input
							prefix="@"
							floatingLabel="Instagram"
							placeholder="Ingrese el Instagram del local"
							value={
								selectedBranch?.instagram?.replaceAll('@', '') ||
								data?.[selectedBranch?.brand]?.info?.instagram?.replaceAll('@', '') ||
								undefined
							}
							Icon={<Instagram className="!text-[1rem]" />}
							validation={instagramValidation}
							transformation={(value) => value.replaceAll('@', '')}
							onChange={(value) => (selectedBranch.instagram = value.trim())}
						/>
						<div className="mt-1 space-y-1">
							<header className="flex items-center gap-2">
								<Clock className="size-5 text-neutral-400" />
								<h2 className="font-medium text-neutral-200">Horario de Atención</h2>
							</header>

							<div className="flex flex-col">
								{['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'].map((day) => (
									<div
										key={day}
										className="flex select-none items-center justify-between rounded-lg p-1.5 transition-colors hover:bg-neutral-900/50"
									>
										<label className="flex items-center gap-1.5 md:gap-3">
											<div className="relative flex items-center">
												<input
													type="checkbox"
													checked={selectedBranch.hours_of_operation?.[day]?.active}
													className="peer size-4 rounded-md border-2 border-neutral-700 bg-neutral-950 transition-colors checked:border-sky-500 checked:bg-sky-500"
													onChange={(e) => {
														selectedBranch.hours_of_operation[day].active = e.target.checked;
														setRerender(rerender + 1);
													}}
												/>
												<div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 transition-opacity peer-checked:opacity-100">
													<svg
														className="size-3 text-white"
														viewBox="0 0 24 24"
														fill="none"
														stroke="currentColor"
														strokeWidth="3"
														strokeLinecap="round"
														strokeLinejoin="round"
													>
														<polyline points="20 6 9 17 4 12" />
													</svg>
												</div>
											</div>
											<span className="text-sm font-medium text-neutral-200 md:text-base">{day}</span>
										</label>

										<div className="flex items-center gap-1">
											<Select
												className="!h-8 w-[4rem] bg-neutral-950 disabled:opacity-50 md:w-[4.25rem]"
												options={Array.from({ length: 8 }, (_, i) => ({
													label: String(i + 4).padStart(2, '0'),
													value: String(i + 4)
												}))}
												defaultValue={selectedBranch.hours_of_operation?.[day]?.from?.toString() || '9'}
												onSelectedChange={(value) => (selectedBranch.hours_of_operation[day].from = Number(value))}
												disabled={!selectedBranch.hours_of_operation?.[day]?.active}
											/>
											<span className="text-sm font-medium text-neutral-300 md:text-base">AM</span>
											<span className="mx-1 shrink-0 text-sm text-neutral-500 md:mx-2 md:text-base">a</span>
											<Select
												className="!h-8 w-[4rem] bg-neutral-950 disabled:opacity-50 md:w-[4.25rem]"
												options={Array.from({ length: 11 }, (_, i) => ({
													label: String(i + 1).padStart(2, '0'),
													value: String(i + 1)
												}))}
												defaultValue={selectedBranch.hours_of_operation?.[day]?.to?.toString() || '5'}
												onSelectedChange={(value) => (selectedBranch.hours_of_operation[day].to = Number(value))}
												disabled={!selectedBranch.hours_of_operation?.[day]?.active}
											/>
											<span className="text-sm font-medium text-neutral-300 md:text-base">PM</span>
										</div>
									</div>
								))}
							</div>
						</div>
					</div>
					<div className="flex justify-end gap-2">
						<Button variant="secondary" className="w-auto" onClick={() => setSelectedBranch(undefined)}>
							Cancelar
						</Button>
						<Button type="submit" variant="primary" className="w-auto">
							Confirmar
						</Button>
					</div>
				</form>
			</Dialog>
		</>
	);
}

export default function Sucursales({
	onboarding,
	updateRegistrationSteps
}: {
	onboarding?: true;
	updateRegistrationSteps?: () => void;
}) {
	const { userData, CountrySettings } = useContext(GlobalContext);
	const { master_store, country } = userData!;

	if (!userData?.master_user) Navigate({ to: '/' });

	const cities = CountrySettings?.cities?.toSorted() || [];

	const [loading, setLoading] = useState(false);
	const {
		data: unsortedData,
		error,
		refetch
	} = useFetch(
		'https://api.krece.app/storeprofile/branch/',
		'GET',
		true,
		{
			email: master_store.id
		},
		setLoading
	);

	const [data, setData] = useState<typeof unsortedData>(null);

	useEffect(() => {
		if (unsortedData && typeof unsortedData === 'object' && Object.keys(unsortedData).length) {
			// Sort brands alphabetically
			const sortedKeys = Object.keys(unsortedData).sort();
			const sortedData = {} as typeof unsortedData;

			// Create sorted data object with brands in alphabetical order
			sortedKeys.forEach((key) => {
				const brand = unsortedData[key as keyof typeof unsortedData];

				// Sort sucursales by store_name if they exist
				if (brand.sucursales && Array.isArray(brand.sucursales) && brand.sucursales.length > 0) {
					brand.sucursales = [...brand.sucursales].sort((a, b) => a.store_name.localeCompare(b.store_name));
				}

				sortedData[key as keyof typeof unsortedData] = brand;
			});

			setData(sortedData);
		} else {
			setData(unsortedData);
		}
	}, [unsortedData]);

	const [selectedBrand, setSelectedBrand] = useState<Brand>();
	const [deleteBrand, setDeleteBrand] = useState<string | null>(null);

	const [selectedBranch, setSelectedBranch] = useState<Sucursal>();
	const [isCreatingBranch, setIsCreatingBranch] = useState(false);
	const [deleteBranch, setDeleteBranch] = useState<{ email: string; is_active: boolean } | null>(null);
	const [reactivateBranch, setReactivateBranch] = useState<{ email: string } | null>(null);
	const [moveBranch, setMoveBranch] = useState<{ email: string; brand: string } | null>(null);
	const [addSellerAccount, setAddSellerAccount] = useState<{ brand: string; store_email: string } | null>(null);
	const [deleteSellerAccount, setDeleteSellerAccount] = useState<{
		brand: string;
		store_email: string;
		seller_account: { email: string; first_name: string; last_name: string };
	} | null>(null);
	const [uploadingImage, setUploadingImage] = useState<false | string>(false);
	const [uploadImageError, setUploadingImageError] = useState<null | string>(null);
	const [permissionsBlacklist, setPermissionsBlacklist] = useState<PermissionsBlacklist>({});
	const [permissionsWhitelist, setPermissionsWhitelist] = useState<PermissionsWhitelist>({});
	const [permissionsError, setPermissionsError] = useState<string | null>(null);
	const [openAccordions, setOpenAccordions] = useState<Record<string, string[]>>({});

	// Listen for permissions changes on master store document
	useEffect(() => {
		const unsubscribe = firestore
			.collection('Stores')
			.doc(master_store.id)
			.onSnapshot(
				(doc) => {
					const data = doc.data();
					setPermissionsBlacklist(data?.seller_permissions_blacklist || {});
					setPermissionsWhitelist(data?.seller_permissions_whitelist || {});
				},
				(error) => {
					console.error('Error listening to permissions:', error);
				}
			);
		return () => unsubscribe();
	}, [master_store.id]);

	const savePermissions = async (newBlacklist: PermissionsBlacklist) => {
		setPermissionsError(null);
		const [, error] = await callAPI(
			'https://api.krece.app/storeprofile/permissions/',
			'PUT',
			{ seller_permissions_blacklist: newBlacklist },
			userData
		);
		if (error) {
			setPermissionsError(error);
		}
	};

	const saveWhitelistPermissions = async (newWhitelist: PermissionsWhitelist) => {
		setPermissionsError(null);
		const [, error] = await callAPI(
			'https://api.krece.app/storeprofile/permissions/',
			'PUT',
			{ seller_permissions_whitelist: newWhitelist },
			userData
		);
		if (error) {
			setPermissionsError(error);
		}
	};

	const PermissionCheckboxGrid = ({
		allowedPermissions,
		enabledPermissions,
		onAllowedChange,
		onEnabledChange,
		globalBlacklist = [],
		globalWhitelist = [],
		disabled = false
	}: {
		allowedPermissions: string[];
		enabledPermissions: string[];
		onAllowedChange: (permissions: string[]) => void;
		onEnabledChange: (permissions: string[]) => void;
		globalBlacklist?: string[];
		globalWhitelist?: string[];
		disabled?: boolean;
	}) => {
		const [blockedDialog, setBlockedDialog] = useState<string | null>(null);

		const toggleBlacklist = (key: string) => {
			if (globalBlacklist.includes(key)) return;
			const newPerms = allowedPermissions.includes(key)
				? allowedPermissions.filter((p) => p !== key)
				: [...allowedPermissions, key];
			onAllowedChange(newPerms);
		};

		const toggleWhitelist = (key: string) => {
			if (globalWhitelist.includes(key)) return;
			const newPerms = enabledPermissions.includes(key)
				? enabledPermissions.filter((p) => p !== key)
				: [...enabledPermissions, key];
			onEnabledChange(newPerms);
		};

		const blockedPermissionLabel = blockedDialog
			? ALL_PERMISSIONS.find((p) => p.key === blockedDialog)?.label
			: null;

		return (
			<>
				{blockedDialog && (
					<Dialog title="Permiso Bloqueado" onClose={() => setBlockedDialog(null)}>
						<p className="text-neutral-300">
							La función <strong className="text-neutral-100">"{blockedPermissionLabel}"</strong> está bloqueada en los{' '}
							<strong className="text-primary-400">Permisos Globales</strong>.
						</p>
						<p className="mt-2 text-sm text-neutral-400">
							Para habilitar esta función, debe activarla en la sección de Permisos Globales ubicada en la parte
							superior de esta página.
						</p>
					</Dialog>
				)}
				<div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
					{ALL_PERMISSIONS.map(({ key, label, type }) => {
						if (type === 'blacklist') {
							const isBlockedGlobally = globalBlacklist.includes(key);
							const isAllowed = allowedPermissions.includes(key) && !isBlockedGlobally;
							const isBlockedLocally = !isAllowed && !isBlockedGlobally;
							return (
								<label
									key={key}
									className={classNames('flex items-center gap-2 rounded-md px-1.5 py-1 text-sm', {
										'bg-red-500/10': isBlockedGlobally,
										'cursor-not-allowed': isBlockedGlobally
									})}
									title={isBlockedGlobally ? 'Bloqueado en Permisos Globales' : undefined}
									onClick={() => isBlockedGlobally && setBlockedDialog(key)}
								>
									<Checkbox
										checked={isAllowed}
										disabled={disabled || isBlockedGlobally}
										onCheckedChange={() => toggleBlacklist(key)}
									/>
									<span
										className={classNames({
											'text-neutral-300': isAllowed,
											'text-neutral-400': isBlockedLocally,
											'text-red-400/70': isBlockedGlobally
										})}
									>
										{label}
									</span>
									{isBlockedGlobally && (
										<span title="Bloqueado en Permisos Globales">
											<Lock className="ml-auto size-3 shrink-0 text-red-400" />
										</span>
									)}
								</label>
							);
						}

						const isEnabledGlobally = globalWhitelist.includes(key);
						const isEnabled = enabledPermissions.includes(key) || isEnabledGlobally;
						return (
							<label
								key={key}
								className={classNames('flex items-center gap-2 rounded-md px-1.5 py-1 text-sm', {
									'cursor-not-allowed bg-green-500/10': isEnabledGlobally
								})}
								title={isEnabledGlobally ? 'Habilitado en Permisos Globales' : undefined}
								onClick={() => isEnabledGlobally && setBlockedDialog(key)}
							>
								<Checkbox
									checked={isEnabled}
									disabled={disabled || isEnabledGlobally}
									onCheckedChange={() => toggleWhitelist(key)}
								/>
								<span className={classNames({ 'text-neutral-300': isEnabled, 'text-neutral-400': !isEnabled })}>
									{label}
								</span>
							</label>
						);
					})}
				</div>
			</>
		);
	};

	const GlobalPermissionsCard = () => {
		const [allowedPermissions, setAllowedPermissions] = useState<string[]>([]);
		const [enabledPermissions, setEnabledPermissions] = useState<string[]>([]);
		const [saving, setSaving] = useState(false);

		useEffect(() => {
			setAllowedPermissions(blacklistToAllowed(permissionsBlacklist?.all_sellers || []));
			setEnabledPermissions(permissionsWhitelist?.all_sellers || []);
		}, []);

		const handleSave = async () => {
			setSaving(true);
			const newBlacklist = allowedToBlacklist(allowedPermissions);
			await Promise.all([
				savePermissions({
					...permissionsBlacklist,
					all_sellers: newBlacklist.length > 0 ? newBlacklist : undefined
				}),
				saveWhitelistPermissions({
					...permissionsWhitelist,
					all_sellers: enabledPermissions.length > 0 ? enabledPermissions : undefined
				})
			]);
			setSaving(false);
		};

		const currentAllowed = blacklistToAllowed(permissionsBlacklist?.all_sellers || []);
		const currentEnabled = permissionsWhitelist?.all_sellers || [];
		const hasChanges =
			JSON.stringify([...allowedPermissions].sort()) !== JSON.stringify([...currentAllowed].sort()) ||
			JSON.stringify([...enabledPermissions].sort()) !== JSON.stringify([...currentEnabled].sort());

		return (
			<section className="mb-6 rounded-xl border border-neutral-800 bg-neutral-900/50 p-4">
				<div className="mb-3 flex items-center gap-2">
					<Lock className="size-5 text-primary-500" />
					<h2 className="font-semibold text-neutral-100">Permisos Globales</h2>
				</div>
				<p className="mb-4 text-sm text-neutral-400">Funciones permitidas para todas las sucursales y vendedores</p>
				<PermissionCheckboxGrid
					allowedPermissions={allowedPermissions}
					enabledPermissions={enabledPermissions}
					onAllowedChange={setAllowedPermissions}
					onEnabledChange={setEnabledPermissions}
				/>
				<div className="mt-4 flex justify-end">
					<Button variant="primary" onClick={handleSave} loading={saving} disabled={!hasChanges}>
						Guardar cambios
					</Button>
				</div>
			</section>
		);
	};

	const SellerPermissionsInline = ({
		account
	}: {
		account: { email: string; first_name: string; last_name: string };
	}) => {
		const globalBlacklist = permissionsBlacklist?.all_sellers || [];
		const globalWhitelist = permissionsWhitelist?.all_sellers || [];
		const [allowedPermissions, setAllowedPermissions] = useState<string[]>([]);
		const [enabledPermissions, setEnabledPermissions] = useState<string[]>([]);
		const [saving, setSaving] = useState(false);

		useEffect(() => {
			setAllowedPermissions(blacklistToAllowed(permissionsBlacklist?.[account.email] || [], globalBlacklist));
			setEnabledPermissions(permissionsWhitelist?.[account.email] || []);
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [account.email]);

		const savePerms = async () => {
			setSaving(true);
			const newBlacklist = { ...permissionsBlacklist };
			const sellerBlacklist = allowedToBlacklist(allowedPermissions).filter((p) => !globalBlacklist.includes(p));
			if (sellerBlacklist.length > 0) {
				newBlacklist[account.email] = sellerBlacklist;
			} else {
				delete newBlacklist[account.email];
			}
			const newWhitelist = { ...permissionsWhitelist };
			const sellerWl = enabledPermissions.filter((p) => !globalWhitelist.includes(p));
			if (sellerWl.length > 0) {
				newWhitelist[account.email] = sellerWl;
			} else {
				delete newWhitelist[account.email];
			}
			await Promise.all([savePermissions(newBlacklist), saveWhitelistPermissions(newWhitelist)]);
			setSaving(false);
		};

		const currentAllowed = blacklistToAllowed(permissionsBlacklist?.[account.email] || [], globalBlacklist);
		const currentEnabled = permissionsWhitelist?.[account.email] || [];
		const hasChanges =
			JSON.stringify([...allowedPermissions].sort()) !== JSON.stringify([...currentAllowed].sort()) ||
			JSON.stringify([...enabledPermissions].sort()) !== JSON.stringify([...currentEnabled].sort());
		const hasRestrictions = (permissionsBlacklist?.[account.email]?.length || 0) > 0;

		return (
			<div>
				<div className="mb-2 flex items-center gap-2">
					<p className="text-sm font-medium text-neutral-200">
						{account.first_name?.trim()} {account.last_name?.trim()}
					</p>
					<span className="text-xs text-neutral-500">({account.email})</span>
					{hasRestrictions && (
						<span className="rounded bg-amber-500/20 px-1.5 py-0.5 text-xs text-amber-400">Restringido</span>
					)}
				</div>
				<PermissionCheckboxGrid
					allowedPermissions={allowedPermissions}
					enabledPermissions={enabledPermissions}
					onAllowedChange={setAllowedPermissions}
					onEnabledChange={setEnabledPermissions}
					globalBlacklist={globalBlacklist}
					globalWhitelist={globalWhitelist}
				/>
				<Button className="mt-3" size="sm" onClick={savePerms} loading={saving} disabled={!hasChanges}>
					Guardar
				</Button>
			</div>
		);
	};

	const BranchPermissionsContent = ({
		branchEmail,
		sellerAccounts
	}: {
		branchEmail: string;
		sellerAccounts?: { email: string; first_name: string; last_name: string }[];
	}) => {
		const globalBlacklist = permissionsBlacklist?.all_sellers || [];
		const globalWhitelist = permissionsWhitelist?.all_sellers || [];

		const [allowedPermissions, setAllowedPermissions] = useState<string[]>([]);
		const [enabledPermissions, setEnabledPermissions] = useState<string[]>([]);
		const [saving, setSaving] = useState(false);

		useEffect(() => {
			setAllowedPermissions(blacklistToAllowed(permissionsBlacklist?.[branchEmail] || [], globalBlacklist));
			setEnabledPermissions(permissionsWhitelist?.[branchEmail] || []);
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [branchEmail]);

		const saveBranchPerms = async () => {
			setSaving(true);
			const newBlacklist = { ...permissionsBlacklist };
			const branchBlacklist = allowedToBlacklist(allowedPermissions).filter((p) => !globalBlacklist.includes(p));
			if (branchBlacklist.length > 0) {
				newBlacklist[branchEmail] = branchBlacklist;
			} else {
				delete newBlacklist[branchEmail];
			}
			const newWhitelist = { ...permissionsWhitelist };
			const branchWl = enabledPermissions.filter((p) => !globalWhitelist.includes(p));
			if (branchWl.length > 0) {
				newWhitelist[branchEmail] = branchWl;
			} else {
				delete newWhitelist[branchEmail];
			}
			await Promise.all([savePermissions(newBlacklist), saveWhitelistPermissions(newWhitelist)]);
			setSaving(false);
		};

		const currentAllowed = blacklistToAllowed(permissionsBlacklist?.[branchEmail] || [], globalBlacklist);
		const currentEnabled = permissionsWhitelist?.[branchEmail] || [];
		const hasChanges =
			JSON.stringify([...allowedPermissions].sort()) !== JSON.stringify([...currentAllowed].sort()) ||
			JSON.stringify([...enabledPermissions].sort()) !== JSON.stringify([...currentEnabled].sort());

		return (
			<div className="space-y-4 p-4 pt-0">
				<div>
					<p className="mb-3 text-sm text-neutral-400">Funciones permitidas para este local</p>
					<PermissionCheckboxGrid
						allowedPermissions={allowedPermissions}
						enabledPermissions={enabledPermissions}
						onAllowedChange={setAllowedPermissions}
						onEnabledChange={setEnabledPermissions}
						globalBlacklist={globalBlacklist}
						globalWhitelist={globalWhitelist}
					/>
					<Button className="mt-3" size="sm" onClick={saveBranchPerms} loading={saving} disabled={!hasChanges}>
						Guardar
					</Button>
				</div>

				{sellerAccounts && sellerAccounts.length > 0 && (
					<div className="border-t border-neutral-800 pt-4">
						<p className="mb-3 text-sm text-neutral-400">Permisos de vendedores</p>
						<div className="space-y-3">
							{sellerAccounts.map((account) => (
								<SellerPermissionsInline key={account.email} account={account} />
							))}
						</div>
					</div>
				)}
			</div>
		);
	};

	const SellerAccountItem = ({
		account,
		brand,
		storeEmail,
		onDelete
	}: {
		account: { email: string; first_name: string; last_name: string };
		brand: string;
		storeEmail: string;
		onDelete: () => void;
	}) => {
		const globalBlacklist = permissionsBlacklist?.all_sellers || [];
		const globalWhitelist = permissionsWhitelist?.all_sellers || [];
		const [allowedPermissions, setAllowedPermissions] = useState<string[]>([]);
		const [enabledPermissions, setEnabledPermissions] = useState<string[]>([]);
		const [editingPerms, setEditingPerms] = useState(false);
		const [saving, setSaving] = useState(false);

		useEffect(() => {
			setAllowedPermissions(blacklistToAllowed(permissionsBlacklist?.[account.email] || [], globalBlacklist));
			setEnabledPermissions(permissionsWhitelist?.[account.email] || []);
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [account.email]);

		const saveSellerPerms = async () => {
			setSaving(true);
			const newBlacklist = { ...permissionsBlacklist };
			const sellerBlacklist = allowedToBlacklist(allowedPermissions).filter((p) => !globalBlacklist.includes(p));
			if (sellerBlacklist.length > 0) {
				newBlacklist[account.email] = sellerBlacklist;
			} else {
				delete newBlacklist[account.email];
			}
			const newWhitelist = { ...permissionsWhitelist };
			const sellerWl = enabledPermissions.filter((p) => !globalWhitelist.includes(p));
			if (sellerWl.length > 0) {
				newWhitelist[account.email] = sellerWl;
			} else {
				delete newWhitelist[account.email];
			}
			await Promise.all([savePermissions(newBlacklist), saveWhitelistPermissions(newWhitelist)]);
			setSaving(false);
		};

		const currentAllowed = blacklistToAllowed(permissionsBlacklist?.[account.email] || [], globalBlacklist);
		const currentEnabled = permissionsWhitelist?.[account.email] || [];
		const hasChanges =
			JSON.stringify([...allowedPermissions].sort()) !== JSON.stringify([...currentAllowed].sort()) ||
			JSON.stringify([...enabledPermissions].sort()) !== JSON.stringify([...currentEnabled].sort());

		const hasRestrictions = (permissionsBlacklist?.[account.email]?.length || 0) > 0;

		return (
			<li className="rounded-lg bg-neutral-925/80 p-2">
				<div className="flex items-center justify-between">
					<div>
						<p className="text-sm font-medium">
							{account.first_name?.trim()} {account.last_name?.trim()}
						</p>
						<p className="text-xs text-neutral-400">{account.email}</p>
					</div>
					<div className="flex items-center gap-1">
						<button
							className={classNames(
								'rounded-lg p-1.5 transition-colors',
								hasRestrictions || editingPerms
									? 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30'
									: 'bg-neutral-800 text-neutral-400 hover:bg-neutral-700'
							)}
							onClick={() => setEditingPerms(!editingPerms)}
							title="Editar permisos"
						>
							<Lock className="size-4" />
						</button>
						<button
							className="rounded-lg bg-red-500/20 p-1.5 text-red-400 transition-colors hover:bg-red-500/30"
							onClick={onDelete}
						>
							<Trash2 className="size-4" />
						</button>
					</div>
				</div>
				{editingPerms && (
					<div className="mt-3 space-y-4 border-t border-neutral-800 pt-3">
						<div>
							<p className="mb-2 text-xs text-neutral-400">Funciones permitidas para este vendedor:</p>
							<PermissionCheckboxGrid
								allowedPermissions={allowedPermissions}
								enabledPermissions={enabledPermissions}
								onAllowedChange={setAllowedPermissions}
								onEnabledChange={setEnabledPermissions}
								globalBlacklist={globalBlacklist}
								globalWhitelist={globalWhitelist}
							/>
							<Button className="mt-3" size="sm" onClick={saveSellerPerms} loading={saving} disabled={!hasChanges}>
								Guardar
							</Button>
						</div>
						<div className="flex">
							<Button variant="ghost" size="sm" onClick={() => setEditingPerms(false)}>
								Cancelar
							</Button>
						</div>
					</div>
				)}
			</li>
		);
	};

	const UpsertBrandDialog = ({ isNew = false, image = false }) => {
		const [counter, setCounter] = useState(0);
		const [submit, setSubmit] = useState(false);
		const { loading, error, setPayload, fetched } = useFetch('https://api.krece.app/storeprofile/brand/', 'POST', submit);

		const isFormValid = () => {
			return (
				selectedBrand &&
				selectedBrand.brand.trim().length >= 4 &&
				validate_phone_number(selectedBrand.whatsapp, country) &&
				(country !== 'peru' || selectedBrand.instagram ? selectedBrand.instagram.trim().length >= 4 : true)
			);
		};

		function submitChanges() {
			if (!selectedBrand || !data || (!isFormValid() && !image)) return;

			const brands: Brand[] = Object.keys(data).map(
				(brand) =>
					({
						brand,
						whatsapp: data[brand].info?.whatsapp || '',
						instagram: data[brand].info?.instagram?.replaceAll('@', '') || '',
						logo: data[brand].info?.logo || null
					}) satisfies Brand
			);

			const brandIndex = brands.findIndex(({ brand }) => brand === selectedBrand.brand);
			if (brandIndex === -1) {
				brands.push({
					...selectedBrand,
					logo: selectedBrand.logo || null
				});
			} else {
				brands[brandIndex] = selectedBrand;
			}

			setPayload({ email: master_store.id, brands });
			setSubmit(true);
		}

		useEffect(() => {
			if (fetched && !error) {
				setSelectedBrand(undefined);
				refetch();
				if (image) setUploadingImage(false);
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [fetched, error]);

		useEffect(() => {
			if (image) submitChanges();
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, []);

		if (!selectedBrand) return null;
		if (image) return null;

		return (
			<>
				{error && (
					<Dialog title="¡Error!" zIndex={52}>
						<p>{typeof error === 'string' ? error : 'Ha ocurrido un error. Intente más tarde.'}</p>
					</Dialog>
				)}

				<Dialog title={isNew ? 'Nueva Tienda' : 'Editar Tienda'} closable={false}>
					<form
						className="space-y-6"
						onSubmit={(e) => {
							e.preventDefault();
							submitChanges();
						}}
					>
						<div className="mt-1 flex flex-col gap-4">
							<Input
								allowedChars="numbers_letters_dot_comma"
								readonly={!isNew}
								value={selectedBrand.brand.trim()}
								floatingLabel="Nombre"
								placeholder="Ingrese el nombre de la tienda"
								Icon={<Building2 className="size-4" />}
								validation={(value) => value.trim().length >= 4}
								onChange={(value) => {
									setCounter(counter + 1);
									selectedBrand.brand = value.trim();
								}}
							/>
							<PhoneInput
								value={selectedBrand.whatsapp}
								country={country}
								onChange={(value) => {
									setCounter(counter + 1);
									selectedBrand.whatsapp = value.trim();
								}}
							/>
							<Input
								prefix="@"
								floatingLabel="Instagram"
								placeholder="Ingrese el Instagram de la tienda"
								value={selectedBrand.instagram?.replaceAll('@', '') || undefined}
								Icon={<Instagram className="!text-[1.2rem]" />}
								validation={(value) => value.trim().length >= 4}
								transformation={(value) => value.replaceAll('@', '')}
								onChange={(value) => {
									setCounter(counter + 1);
									selectedBrand.instagram = value.trim();
								}}
							/>
						</div>
						<div className="flex justify-end gap-2">
							<Button
								disabled={loading}
								variant="default"
								className="bg-neutral-900 hover:bg-neutral-900/70"
								onClick={() => setSelectedBrand(undefined)}
							>
								Cancelar
							</Button>
							<Button
								disabled={!isFormValid()}
								loading={loading}
								type="submit"
								variant="default"
								className="bg-sky-600 hover:bg-sky-800"
							>
								Confirmar
							</Button>
						</div>
					</form>
				</Dialog>
			</>
		);
	};

	const AddSellerAccountDialog = ({ brand, store_email }: { brand: string; store_email: string }) => {
		const [submit, setSubmit] = useState(false);
		const { loading, error, setPayload, fetched } = useFetch('https://api.krece.app/storeprofile/branch/seller/', 'POST', submit);

		const [email, setEmail] = useState('');
		const [first_name, setFirstName] = useState('');
		const [last_name, setLastName] = useState('');

		const is_form_valid = validate_email(email) && first_name.trim().length >= 3 && last_name.trim().length >= 3;

		useEffect(() => {
			if (fetched && !error) {
				setAddSellerAccount(null);
				refetch();
			}
		}, [fetched, error]);

		return (
			<>
				{error && (
					<Dialog title="¡Error!" zIndex={52}>
						<p>{typeof error === 'string' ? error : 'Ha ocurrido un error. Intente más tarde.'}</p>
					</Dialog>
				)}
				<Dialog title="Agregar Cuenta de Vendedor" closable={false}>
					<form
						className="space-y-6"
						onSubmit={(e) => {
							e.preventDefault();
							setPayload({
								brand,
								store_email,
								seller_account: {
									email: email.trim().toLowerCase(),
									first_name: first_name.trim(),
									last_name: last_name.trim()
								}
							});
							setSubmit(true);
						}}
					>
						<div className="mt-1 flex flex-col gap-3">
							<Input
								type="email"
								floatingLabel="Email de la cuenta"
								placeholder="Email para la cuenta"
								Icon={<Mail className="size-4" />}
								value={email}
								validation={validate_email}
								onChange={(value) => setEmail(value.trim())}
							/>
							<Input
								floatingLabel="Nombre"
								placeholder="Nombre del vendedor"
								value={first_name}
								validation={(value) => value.trim().length >= 3}
								onChange={(value) => setFirstName(value.trim())}
							/>
							<Input
								floatingLabel="Apellidos"
								placeholder="Apellidos del vendedor"
								value={last_name}
								validation={(value) => value.trim().length >= 3}
								onChange={(value) => setLastName(value.trim())}
							/>
						</div>
						<div className="flex justify-end gap-2">
							<Button variant="secondary" className="w-auto" onClick={() => setAddSellerAccount(null)}>
								Cancelar
							</Button>
							<Button type="submit" variant="primary" className="w-auto" disabled={!is_form_valid} loading={loading}>
								Confirmar
							</Button>
						</div>
					</form>
				</Dialog>
			</>
		);
	};

	const DeleteSellerAccountDialog = ({
		brand,
		store_email,
		seller_account
	}: {
		brand: string;
		store_email: string;
		seller_account: { email: string; first_name: string; last_name: string };
	}) => {
		const [submit, setSubmit] = useState(false);
		const { loading, error, fetched } = useFetch('https://api.krece.app/storeprofile/branch/seller/', 'DELETE', submit, {
			brand,
			store_email: store_email,
			seller_account: seller_account
		});

		useEffect(() => {
			if (fetched && !error) {
				setDeleteSellerAccount(null);
				refetch();
			}
		}, [fetched, error]);

		return (
			<>
				{error && (
					<Dialog title="¡Error!" zIndex={52}>
						<p>{typeof error === 'string' ? error : 'Ha ocurrido un error. Intente más tarde.'}</p>
					</Dialog>
				)}

				<Dialog title="Eliminar Tienda" closable={false}>
					<div className="space-y-6">
						<p>
							¿Está seguro que desea eliminar la cuenta de vendedor <strong>{seller_account.email}</strong>?
						</p>
						<div className="flex justify-end gap-2">
							<Button variant="secondary" className="w-auto" onClick={() => setDeleteSellerAccount(null)} disabled={loading}>
								Cancelar
							</Button>
							<Button
								loading={loading}
								type="submit"
								variant="primary"
								className="w-auto !bg-red-500 hover:!bg-red-700"
								onClick={() => setSubmit(true)}
							>
								Eliminar
							</Button>
						</div>
					</div>
				</Dialog>
			</>
		);
	};

	const DeleteBrandDialog = ({ brand }: { brand: string }) => {
		const [submit, setSubmit] = useState(false);
		const { loading, error, fetched } = useFetch('https://api.krece.app/storeprofile/brand/', 'DELETE', submit, {
			brand_name: brand
		});

		useEffect(() => {
			if (fetched && !error) {
				setDeleteBrand(null);
				refetch();
			}
		}, [fetched, error]);

		return (
			<>
				{error && (
					<Dialog title="¡Error!" zIndex={52}>
						<p>{typeof error === 'string' ? error : 'Ha ocurrido un error. Intente más tarde.'}</p>
					</Dialog>
				)}

				<Dialog title="Eliminar Tienda" closable={false}>
					<div className="space-y-6">
						<p>
							¿Está seguro que desea eliminar la tienda <strong>{brand}</strong>?
						</p>
						<div className="flex justify-end gap-2">
							<Button variant="secondary" className="w-auto" onClick={() => setDeleteBrand(null)} disabled={loading}>
								Cancelar
							</Button>
							<Button
								loading={loading}
								type="submit"
								variant="primary"
								className="w-auto !bg-red-500 hover:!bg-red-700"
								onClick={() => setSubmit(true)}
							>
								Eliminar
							</Button>
						</div>
					</div>
				</Dialog>
			</>
		);
	};

	const DeleteSucursalDialog = () => {
		const [submit, setSubmit] = useState(false);
		const { loading, error, fetched } = useFetch('https://api.krece.app/storeprofile/branch/', 'DELETE', submit, {
			store_email: deleteBranch!.email,
			email: master_store.id
		});

		useEffect(() => {
			if (fetched && !error) {
				setDeleteBranch(null);
				refetch();
				// window.location.reload();
			}
		}, [fetched, error]);

		return (
			<>
				{error && (
					<Dialog title="¡Error!" zIndex={52}>
						<p>{typeof error === 'string' ? error : 'Ha ocurrido un error. Intente más tarde.'}</p>
					</Dialog>
				)}
				<Dialog title={deleteBranch?.is_active ? 'Desactivar Local' : 'Eliminar Local'} closable={false}>
					{loading && (
						<div className="absolute inset-0 z-50 flex h-full w-full items-center justify-center rounded-xl bg-black/70">
							<ContainerSpinner size={2.75} />
						</div>
					)}
					<div className="space-y-6">
						<p className="mb-2">
							¿Está seguro que desea {deleteBranch?.is_active ? 'desactivar el local ' : 'eliminar el local y su cuenta '}
							asociada con el email <strong>{deleteBranch?.email}</strong>?
						</p>
						{deleteBranch?.is_active ? (
							<span className="rounded-lg border border-yellow-400/90 bg-yellow-900/20 p-1.5 text-sm font-bold text-yellow-400 light:bg-yellow-500/50 light:text-yellow-100">
								Podrá reactivarla más adelante si desea
							</span>
						) : (
							<span className="rounded-lg border border-red-400/90 bg-red-900/20 p-1.5 text-sm font-bold text-red-400">
								¡Esta acción es irreversible!
							</span>
						)}
						<div className="flex justify-end gap-2">
							<Button variant="default" className="bg-neutral-900 hover:bg-neutral-900/70" onClick={() => setDeleteBranch(null)}>
								Cancelar
							</Button>
							<Button
								type="submit"
								variant="default"
								className={classNames({
									'bg-red-500 text-red-100 hover:bg-red-700': !deleteBranch?.is_active,
									'bg-yellow-500/80 text-yellow-100 hover:bg-yellow-700': deleteBranch?.is_active
								})}
								onClick={() => setSubmit(true)}
							>
								{deleteBranch?.is_active ? 'Desactivar' : 'Eliminar'}
							</Button>
						</div>
					</div>
				</Dialog>
			</>
		);
	};

	const ReactivateSucursalDialog = () => {
		return (
			<Dialog title="Reactivar Local" closable={false}>
				<div className="space-y-6">
					<p className="mb-2">Para reactivar este local, por favor contacte al equipo de Krece.</p>

					<div className="flex justify-end gap-2">
						<Button
							variant="default"
							className="bg-neutral-900 hover:bg-neutral-900/70"
							onClick={() => setReactivateBranch(null)}
						>
							Salir
						</Button>
					</div>
				</div>
			</Dialog>
		);
	};

	const MoveBranchDialog = () => {
		const [newBrand, setNewBrand] = useState<string | null>(null);

		const [submit, setSubmit] = useState(false);
		const { loading, error, setPayload, fetched } = useFetch('https://api.krece.app/storeprofile/branch/move/', 'POST', submit);

		useEffect(() => {
			if (fetched && !error) {
				setMoveBranch(null);
				refetch();
			}
		}, [fetched, error]);

		if (!data || !moveBranch) return null;
		return (
			<>
				{error && (
					<Dialog title="¡Error!" zIndex={52}>
						<p>{typeof error === 'string' ? error : 'Ha ocurrido un error. Intente más tarde.'}</p>
					</Dialog>
				)}
				<Dialog title="Mover Local" closable={false}>
					<div className="space-y-4">
						<p>Seleccione la tienda a la que desea mover este local:</p>
						<Select
							defaultValue={moveBranch.brand}
							options={Object.keys(data).map((brand) => ({ label: brand, value: brand }))}
							onSelectedChange={(value) => setNewBrand(value as string)}
						/>
						<div className="flex justify-end gap-2">
							<Button variant="default" className="bg-neutral-900 hover:bg-neutral-900/70" onClick={() => setMoveBranch(null)}>
								Cancelar
							</Button>
							<Button
								type="submit"
								variant="primary"
								className="w-auto"
								disabled={!newBrand || moveBranch.brand === newBrand}
								loading={loading}
								onClick={() => {
									setSubmit(true);
									setPayload({
										email: master_store.id,
										store_email: moveBranch.email,
										current_brand: moveBranch.brand,
										new_brand: newBrand!
									});
								}}
							>
								Confirmar
							</Button>
						</div>
					</div>
				</Dialog>
			</>
		);
	};

	const Sucursal = ({ data, brand }: { data: Sucursal; brand: Brand }) => {
		const branchAccordions = openAccordions[data.store_email] || [];
		const setBranchAccordions = (values: string[]) => {
			setOpenAccordions((prev) => ({ ...prev, [data.store_email]: values }));
		};

		// Check if branch or any seller has restrictions
		const branchHasRestrictions = (permissionsBlacklist?.[data.store_email]?.length || 0) > 0;
		const sellersWithRestrictions = data.seller_accounts?.some(
			(account) => (permissionsBlacklist?.[account.email]?.length || 0) > 0
		);
		const hasAnyRestrictions = branchHasRestrictions || sellersWithRestrictions;

		return (
			<li>
			<article className={classNames('rounded-xl bg-neutral-950/70 light:bg-neutral-1000')}>
				<header className="flex w-full items-center justify-between gap-1 border-b border-neutral-900 p-2 sm:gap-2 md:p-4">
					<div className="flex items-center gap-1 sm:gap-2">
						<div
							onClick={(e) => (e.currentTarget.lastElementChild as HTMLInputElement).click()}
							className={classNames(
								'group relative flex size-10 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-full bg-neutral-900 transition-colors hover:bg-primary-500 sm:size-12',
								{
									'pointer-events-none': uploadingImage === data?.store_email || !data?.is_active,
									'hover:!bg-black': data?.logo || brand.logo
								}
							)}
						>
							{uploadingImage === data?.store_email && <Spinner />}
							{uploadingImage !== data?.store_email && (
								<>
									{(data?.logo || brand?.logo) && (
										<img
											src={data?.logo || brand?.logo || ''}
											alt="Logo del Local"
											className={classNames('size-full object-cover transition-opacity group-hover:opacity-40', {
												'opacity-40': !data?.is_active
											})}
										/>
									)}
									<ImageUp
										className={classNames(
											'size-6 transition-all group-hover:scale-110',
											{
												'absolute opacity-0 group-hover:opacity-100 light:text-white': data?.logo || brand.logo
											},
											{
												'opacity-0': !data?.is_active
											}
										)}
									/>
								</>
							)}

							<input
								type="file"
								accept="image/*"
								className="hidden"
								onChange={async (e) => {
									if (uploadingImage) return;
									setUploadingImage(data?.store_email);

									const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/bmp', 'image/tiff', 'image/webp'];

									try {
										const image = e.currentTarget.files?.[0];
										if (!image) throw new Error();

										const is_image = image.size && IMAGE_TYPES.includes(image.type);
										const extension = image.name.split('.')?.pop()?.toLowerCase();
										const size = image.size;

										if (!is_image || !extension) {
											// eslint-disable-next-line no-throw-literal
											throw 'Debe subir una imagen válida.';
										} else if (size > 3000000) {
											// eslint-disable-next-line no-throw-literal
											throw 'El tamaño de la imagen seleccionada no puede superar los 3MB.';
										}

										const storage = Firebase.storage();
										const storageRef = storage.ref();
										const fileName = `${Date.now()}_${data?.store_email}.${extension}`;
										const fileRef = storageRef.child(fileName);
										await fileRef.put(image, {
											cacheControl: 'public,max-age=31536000,immutable'
										});

										const downloadURL = await fileRef.getDownloadURL();
										if (downloadURL) {
											setIsCreatingBranch(false);
											setSelectedBranch({
												store_email: data.store_email,
												city: data.city,
												hours_of_operation: data.hours_of_operation,
												location: data.location,
												location_coordinates: data.location_coordinates,
												location_details: data.location_details,
												store_name: data.store_name,
												brand: data.brand,
												logo: downloadURL
											});
										} else {
											throw new Error();
										}
									} catch (error) {
										setUploadingImageError(typeof error === 'string' ? error : 'Error al subir la imagen.');
										setUploadingImage(false);
									}
								}}
							/>
						</div>
						<div className="flex flex-col">
							<h3
								className={classNames(
									'overflow-hidden overflow-ellipsis text-nowrap text-lg font-medium leading-none tracking-tight',
									{
										'opacity-40': !data?.is_active
									}
								)}
							>
								{data?.store_name}
							</h3>
							<p className="overflow-hidden overflow-ellipsis text-nowrap text-sm tracking-tight text-neutral-300/90">
								{data?.city}
							</p>
						</div>
					</div>
					<div className="flex shrink-0 items-center justify-end gap-2">
						{data.is_active ? (
							<>
								<button
									className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-neutral-900 transition-opacity hover:opacity-60"
									onClick={() => setMoveBranch({ email: data.store_email, brand: data.brand })}
								>
									<ArrowUpDown className="size-[1.1rem]" />
								</button>
								<button
									className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-neutral-900 transition-opacity hover:opacity-60"
									onClick={() => {
										setIsCreatingBranch(false);
										setSelectedBranch({
											store_email: data.store_email,
											city: data.city,
											hours_of_operation: data.hours_of_operation,
											location: data.location,
											location_coordinates: data.location_coordinates,
											location_details: data.location_details,
											store_name: data.store_name,
											brand: data.brand,
											logo: data?.logo || null
										});
									}}
								>
									<Edit className="size-[1.1rem]" />
								</button>
								<button
									className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-green-500/60 transition-all hover:bg-yellow-500/70"
									onClick={() => setDeleteBranch({ email: data.store_email, is_active: true })}
								>
									<Power className="size-[1.1rem]" strokeWidth={2.5} />
								</button>
							</>
						) : (
							<>
								<button
									className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-red-500/70 transition-opacity hover:opacity-60"
									onClick={() => setDeleteBranch({ email: data.store_email, is_active: false })}
								>
									<Trash2 className="size-[1.1rem]" />
								</button>
								<button
									className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-yellow-500/70 transition-all hover:bg-green-500/60"
									onClick={() => setReactivateBranch({ email: data.store_email })}
								>
									<Power className="size-[1.1rem]" strokeWidth={2.5} />
								</button>
							</>
						)}
					</div>
				</header>

				<div className="flex flex-col">
					<ul
						className={classNames(
							'col-span-2 hidden w-full grid-cols-2 content-center p-2 text-sm text-neutral-200/90 md:grid md:p-4',
							{
								'opacity-65 grayscale': !data?.is_active
							}
						)}
					>
						<li className="flex items-center gap-2">
							<Mail className="size-4 shrink-0 text-neutral-600" />
							<p className="overflow-hidden overflow-ellipsis text-nowrap">{data.store_email}</p>
						</li>
						<li className="flex items-center gap-2">
							<Instagram className="shrink-0 !text-[1rem] text-neutral-600" />
							<p className="overflow-hidden overflow-ellipsis text-nowrap">
								@{data?.instagram?.replaceAll('@', '') || brand?.instagram?.replaceAll('@', '') || 'N/A'}
							</p>
						</li>
						<li className="flex items-center gap-2">
							<WhatsApp className="shrink-0 !text-[1rem] text-neutral-600" />
							<p className="overflow-hidden overflow-ellipsis text-nowrap">{data?.whatsapp || brand?.whatsapp || 'N/A'}</p>
						</li>
						<li className="flex items-center gap-2">
							<MapPin className="size-4 shrink-0 text-neutral-600" />
							<p className="overflow-hidden overflow-ellipsis text-nowrap">
								{data?.location_details || data?.location || 'N/A'}
							</p>
						</li>
					</ul>

					<Accordion.Root type="multiple" className="w-full" value={branchAccordions} onValueChange={setBranchAccordions}>
						<Accordion.Item value="sellers">
							<Accordion.Trigger className="group flex w-full items-center justify-between border-t border-neutral-900 px-4 py-3 text-sm font-medium text-neutral-400 hover:text-neutral-200">
								<div className="flex items-center gap-2">
									Cuentas de Vendedores
									<span className="rounded-full bg-neutral-900 px-2 py-0.5 text-xs">{data.seller_accounts?.length || 0}</span>
								</div>
								<ChevronDown className="size-4 transition-transform group-data-[state=open]:rotate-180" />
							</Accordion.Trigger>
							<Accordion.Content className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
								<div className="space-y-4 p-4 pt-0">
									<ul className="space-y-2">
										{data.seller_accounts?.map((account) => (
											<SellerAccountItem
												key={account.email}
												account={account}
												brand={brand.brand}
												storeEmail={data.store_email}
												onDelete={() =>
													setDeleteSellerAccount({
														brand: brand.brand,
														store_email: data.store_email,
														seller_account: account
													})
												}
											/>
										))}
										{!data.seller_accounts?.length && (
											<p className="text-center text-neutral-300">No hay cuentas de vendedores aún...</p>
										)}
									</ul>
									<button
										onClick={() => setAddSellerAccount({ brand: brand.brand, store_email: data.store_email })}
										className="flex items-center gap-1 justify-self-end rounded-lg bg-neutral-900 px-2 py-1 text-xs font-medium text-neutral-200 transition-opacity hover:opacity-80"
									>
										<Plus className="size-3" />
										Agregar
									</button>
								</div>
							</Accordion.Content>
						</Accordion.Item>
						<Accordion.Item value="permissions">
							<Accordion.Trigger className="group flex w-full items-center justify-between border-t border-neutral-900 px-4 py-3 text-sm font-medium text-neutral-400 hover:text-neutral-200">
								<div className="flex items-center gap-2">
									<Lock className="size-4" />
									Permisos del Local
									{hasAnyRestrictions && (
										<span className="rounded bg-amber-500/20 px-1.5 py-0.5 text-xs text-amber-400">
											Restringido
										</span>
									)}
								</div>
								<ChevronDown className="size-4 transition-transform group-data-[state=open]:rotate-180" />
							</Accordion.Trigger>
							<Accordion.Content className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
								<BranchPermissionsContent branchEmail={data.store_email} sellerAccounts={data.seller_accounts} />
							</Accordion.Content>
						</Accordion.Item>
					</Accordion.Root>
				</div>
			</article>
		</li>
		);
	};

	const Brand = ({
		brand,
		whatsapp,
		instagram,
		sucursales,
		logo
	}: Brand & {
		sucursales: Sucursal[];
	}) => (
		<section key={brand} className="overflow-hidden rounded-xl border border-neutral-900 bg-neutral-925/80">
			<header className="flex shrink-0 items-center justify-between gap-1 border-b border-neutral-900 bg-neutral-950/80 p-3 sm:p-4 md:gap-2">
				<h1 className="flex items-center gap-2 sm:gap-4">
					{
						<div
							onClick={(e) => (e.currentTarget.lastElementChild as HTMLInputElement).click()}
							className={classNames(
								'group relative flex size-14 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-full bg-neutral-900 transition-colors hover:bg-primary-500 sm:size-16',
								{ 'pointer-events-none': uploadingImage === brand, 'hover:!bg-black': logo }
							)}
						>
							{uploadingImage === brand && <Spinner />}
							{uploadingImage !== brand && (
								<>
									{logo && (
										<img
											src={logo}
											alt="Logo de la Tienda"
											className="size-full object-cover transition-opacity group-hover:opacity-40"
										/>
									)}
									<ImageUp
										className={classNames('size-7 transition-all group-hover:scale-110', {
											'absolute opacity-0 group-hover:opacity-100 light:text-white': logo
										})}
									/>
								</>
							)}

							<input
								type="file"
								accept="image/*"
								className="hidden"
								onChange={async (e) => {
									if (uploadingImage) return;
									setUploadingImage(brand);

									const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/bmp', 'image/tiff', 'image/webp'];

									try {
										const image = e.currentTarget.files?.[0];
										if (!image) throw new Error();

										const is_image = image.size && IMAGE_TYPES.includes(image.type);
										const extension = image.name.split('.')?.pop()?.toLowerCase();
										const size = image.size;

										if (!is_image || !extension) {
											// eslint-disable-next-line no-throw-literal
											throw 'Debe subir una imagen válida.';
										} else if (size > 3000000) {
											// eslint-disable-next-line no-throw-literal
											throw 'El tamaño de la imagen seleccionada no puede superar los 3MB.';
										}

										const storage = Firebase.storage();
										const storageRef = storage.ref();
										const fileName = `${Date.now()}_${brand}.${extension}`;
										const fileRef = storageRef.child(fileName);
										await fileRef.put(image, {
											cacheControl: 'public,max-age=31536000,immutable'
										});

										const downloadURL = await fileRef.getDownloadURL();
										if (downloadURL) {
											setSelectedBrand({
												brand,
												instagram: instagram?.replaceAll('@', '') || '',
												whatsapp: whatsapp || '',
												logo: downloadURL
											});
										} else {
											throw new Error();
										}
									} catch (error) {
										setUploadingImageError(typeof error === 'string' ? error : 'Error al subir la imagen.');
										setUploadingImage(false);
									}
								}}
							/>
						</div>
					}
					<div className="flex flex-col gap-1 p-1 md:gap-2">
						<h2 className="flex items-center gap-3 text-lg font-bold tracking-tight text-neutral-0 md:text-xl lg:text-2xl">
							{brand}
						</h2>
						<div className="flex flex-wrap items-center gap-1 text-sm text-neutral-300/90 md:flex-nowrap md:gap-4">
							<div className="group flex items-center gap-1 transition-colors hover:text-neutral-100 md:gap-2">
								<div className="flex items-center justify-center rounded-full bg-red-500/10 p-0.5 md:p-1">
									<Instagram className="!text-[1rem] text-red-400 md:!text-[1.25rem]" />
								</div>
								{instagram ? (
									<span className="truncate font-medium">@{instagram?.replaceAll('@', '')}</span>
								) : (
									<span className="truncate rounded-md bg-red-500/20 px-2 py-0.5 text-xs font-medium text-red-300 light:bg-red-500/10 light:text-red-400">
										{country !== 'peru' ? 'Requerido' : 'N/A'}
									</span>
								)}
							</div>
							<div className="group flex items-center gap-1 transition-colors hover:text-neutral-100 md:gap-2">
								<div className="flex items-center justify-center rounded-full bg-green-500/10 p-0.5 md:p-1">
									<WhatsApp className="!text-[1rem] text-green-400 md:!text-[1.25rem]" />
								</div>
								{whatsapp ? (
									<span className="truncate font-medium">{whatsapp}</span>
								) : (
									<span className="truncate rounded-md bg-red-500/20 px-2 py-0.5 text-xs font-medium text-red-300 light:bg-red-500/10 light:text-red-400">
										Requerido
									</span>
								)}
							</div>
						</div>
					</div>
				</h1>
				<div className="flex shrink-0 items-center gap-2">
					{!sucursales.length && Object.keys(data || {}).length > 1 && (
						<Button variant="destructive" className="!size-10 !p-0" onClick={() => setDeleteBrand(brand)}>
							<Trash2 className="size-5" />
						</Button>
					)}
					<Button
						className="!size-10 shrink-0 bg-neutral-900 !p-0"
						onClick={() =>
							setSelectedBrand({
								brand: brand || '',
								instagram: instagram?.replaceAll('@', '') || '',
								whatsapp: whatsapp || '',
								logo: logo || ''
							})
						}
					>
						<Edit className="size-5" strokeWidth={2.25} />
					</Button>
					<Button
						className="size-10 shrink-0 bg-primary-500/20 font-medium text-primary-200 transition-colors hover:bg-primary-500/20 light:text-primary-600 light:hover:bg-primary-500/40 md:size-auto md:h-10"
						onClick={() => {
							setIsCreatingBranch(true);
							setSelectedBranch({
								store_email: '',
								city: '',
								hours_of_operation: {
									Lunes: { active: true, from: 9, to: 5 },
									Martes: { active: true, from: 9, to: 5 },
									Miércoles: { active: true, from: 9, to: 5 },
									Jueves: { active: true, from: 9, to: 5 },
									Viernes: { active: true, from: 9, to: 5 },
									Sábado: { active: true, from: 9, to: 5 },
									Domingo: { active: true, from: 9, to: 5 }
								},
								location: '',
								location_coordinates: undefined,
								location_details: '',
								store_name: '',
								brand,
								logo: null
							});
						}}
					>
						<Plus className="size-4 scale-[2.75] md:scale-100" strokeWidth={2.5} />
						<span className="hidden md:inline-flex">Local</span>
					</Button>
				</div>
			</header>
			<ol className="flex flex-col gap-4 p-4">
				{sucursales.map((branch) => (
					<Sucursal
						key={branch.store_email}
						data={branch}
						brand={{ brand, logo, instagram: instagram?.replaceAll('@', ''), whatsapp }}
					/>
				))}
				{!sucursales.length && <p className="text-center text-neutral-300">No hay locales aún...</p>}
			</ol>
		</section>
	);

	if (onboarding) {
		return (
			<>
				{loading && <ContainerSpinner size={3} className="mt-4" />}
				{error && <ErrorRedirectDialog />}

				{selectedBranch && (
					<UpsertSucursalDialog
						isNew={true}
						selectedBranch={selectedBranch}
						setSelectedBranch={setSelectedBranch}
						onboarding={onboarding}
						updateRegistrationSteps={updateRegistrationSteps}
						refetch={refetch}
						masterStoreId={master_store.id}
						country={country}
						cities={cities}
						data={data}
						setUploadingImage={setUploadingImage}
					/>
				)}

				{data && !loading && !error && (
					<div className="mx-auto flex h-full max-w-5xl flex-col items-center justify-center gap-4 p-4 text-center">
						<p className="text-neutral-300">Agregue un local para comenzar</p>
						<Button
							variant="primary"
							className="w-auto gap-1.5"
							onClick={() => {
								setIsCreatingBranch(true);
								setSelectedBranch({
									store_email: '',
									city: '',
									hours_of_operation: {
										Lunes: { active: true, from: 9, to: 5 },
										Martes: { active: true, from: 9, to: 5 },
										Miércoles: { active: true, from: 9, to: 5 },
										Jueves: { active: true, from: 9, to: 5 },
										Viernes: { active: true, from: 9, to: 5 },
										Sábado: { active: true, from: 9, to: 5 },
										Domingo: { active: true, from: 9, to: 5 }
									},
									location: '',
									location_coordinates: undefined,
									location_details: '',
									store_name: '',
									brand: Object.keys(data)[0],
									logo: null
								});
							}}
						>
							<Plus className="size-4" strokeWidth={2.5} />
							Añadir Local
						</Button>
					</div>
				)}
			</>
		);
	}

	return (
		<>
			{loading && !data && (
				<div className="mx-auto max-w-[52rem] space-y-6 p-4 md:p-[1.25rem]">
					<div className="flex items-center justify-between px-1">
						<div className="flex items-center gap-3">
							<div className="size-7 animate-pulse rounded-lg bg-neutral-800/50" />
							<div className="h-8 w-40 animate-pulse rounded-lg bg-neutral-800/60" />
						</div>
						<div className="h-10 w-36 animate-pulse rounded-lg bg-neutral-800/40" />
					</div>
					<div className="animate-pulse rounded-xl border border-neutral-800/60 bg-neutral-900/50 p-4 space-y-3">
						<div className="flex items-center gap-2">
							<div className="size-5 rounded bg-neutral-800/50" />
							<div className="h-5 w-40 rounded bg-neutral-800/50" />
						</div>
						<div className="h-4 w-72 rounded bg-neutral-800/30" />
						<div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
							{[1, 2, 3, 4, 5].map((i) => (
								<div key={i} className="h-7 rounded-md bg-neutral-800/30" />
							))}
						</div>
					</div>
					{[1, 2].map((brand) => (
						<div key={brand} className="overflow-hidden rounded-xl border border-neutral-900 bg-neutral-925/80">
							<div className="flex items-center justify-between border-b border-neutral-900 bg-neutral-950/80 p-4">
								<div className="flex items-center gap-4">
									<div className="size-14 animate-pulse rounded-full bg-neutral-800/50" />
									<div className="space-y-2">
										<div className="h-6 w-32 animate-pulse rounded bg-neutral-800/50" />
										<div className="flex gap-4">
											<div className="h-4 w-28 animate-pulse rounded bg-neutral-800/30" />
											<div className="h-4 w-28 animate-pulse rounded bg-neutral-800/30" />
										</div>
									</div>
								</div>
								<div className="flex gap-2">
									<div className="size-10 animate-pulse rounded-lg bg-neutral-800/40" />
									<div className="size-10 animate-pulse rounded-lg bg-neutral-800/40" />
								</div>
							</div>
							<div className="flex flex-col gap-4 p-4">
								{[1, 2].map((branch) => (
									<div key={branch} className="rounded-xl bg-neutral-950/70 p-4 space-y-3">
										<div className="flex items-center justify-between">
											<div className="flex items-center gap-2">
												<div className="size-10 animate-pulse rounded-full bg-neutral-800/50" />
												<div className="space-y-1.5">
													<div className="h-5 w-28 animate-pulse rounded bg-neutral-800/50" />
													<div className="h-3.5 w-20 animate-pulse rounded bg-neutral-800/30" />
												</div>
											</div>
											<div className="flex gap-2">
												<div className="size-8 animate-pulse rounded-lg bg-neutral-800/40" />
												<div className="size-8 animate-pulse rounded-lg bg-neutral-800/40" />
												<div className="size-8 animate-pulse rounded-lg bg-neutral-800/40" />
											</div>
										</div>
									</div>
								))}
							</div>
						</div>
					))}
				</div>
			)}
			{error && <ErrorRedirectDialog />}
			{uploadImageError && !error && (
				<Dialog title="" onClose={() => setUploadingImageError(null)}>
					{uploadImageError}
				</Dialog>
			)}

			{selectedBrand && <UpsertBrandDialog isNew={selectedBrand.brand ? false : true} image={uploadingImage ? true : false} />}
			{deleteBrand && <DeleteBrandDialog brand={deleteBrand} />}

			{selectedBranch && (
				<UpsertSucursalDialog
					isNew={isCreatingBranch}
					image={uploadingImage ? true : false}
					selectedBranch={selectedBranch}
					setSelectedBranch={setSelectedBranch}
					onboarding={onboarding}
					updateRegistrationSteps={updateRegistrationSteps}
					refetch={refetch}
					masterStoreId={master_store.id}
					country={country}
					cities={cities}
					data={data}
					setUploadingImage={setUploadingImage}
				/>
			)}
			{deleteBranch && <DeleteSucursalDialog />}
			{reactivateBranch && <ReactivateSucursalDialog />}
			{moveBranch && <MoveBranchDialog />}
			{addSellerAccount && <AddSellerAccountDialog brand={addSellerAccount.brand} store_email={addSellerAccount.store_email} />}
			{deleteSellerAccount && (
				<DeleteSellerAccountDialog
					brand={deleteSellerAccount.brand}
					store_email={deleteSellerAccount.store_email}
					seller_account={deleteSellerAccount.seller_account}
				/>
			)}
			{permissionsError && (
				<Dialog title="Error" onClose={() => setPermissionsError(null)}>
					<p>{permissionsError}</p>
				</Dialog>
			)}

			{data && !loading && !error && (
				<div className="mx-auto max-w-[52rem] space-y-6 p-4 md:p-[1.25rem]">
					<div className="flex items-center justify-between px-1">
						<h1 className="flex items-center gap-3 text-2xl font-bold tracking-tight text-neutral-0 3xl:text-3xl">
							<Store className="size-7 text-primary-200 light:text-primary-600" strokeWidth={2} />
							Mis Tiendas
						</h1>
						<Button
							variant="default"
							className="flex items-center gap-2 rounded-lg bg-primary-500/20 px-4 py-2.5 font-medium text-primary-200 transition-colors hover:bg-primary-500/30 light:text-primary-600 light:hover:bg-primary-500/40"
							onClick={() =>
								setSelectedBrand({
									brand: '',
									whatsapp: '',
									instagram: '',
									logo: null
								})
							}
						>
							<Plus className="size-4" strokeWidth={2.5} />
							Añadir tienda
						</Button>
					</div>

					<GlobalPermissionsCard />

					{Object.keys(data).map((brand) => (
						<Brand
							key={brand}
							brand={brand}
							whatsapp={data[brand].info.whatsapp}
							instagram={data[brand].info.instagram?.replaceAll('@', '')}
							sucursales={data[brand].sucursales.map((e) => ({ ...e, brand }))}
							logo={data[brand].info.logo || null}
						/>
					))}
				</div>
			)}
		</>
	);
}

type Sucursal = Omit<API['https://api.krece.app/storeprofile/branch/']['POST']['request'], 'email'> &
	API['https://api.krece.app/storeprofile/branch/']['GET']['response'][string]['sucursales'][number];

type Brand = API['https://api.krece.app/storeprofile/brand/']['GET']['response'][number];
